// Focus-trap math for the `Modal` base (T180 a11y hardening). Pure — no DOM
// mutation, no IO — so the wrap decision is unit-testable in the node vitest
// env. The DOM wiring (event listener, initial-focus, restore-focus) lives in
// `app/components/ui/Modal.tsx` and is exercised by the dashboard E2E.

/**
 * Selector for the elements a focus trap should cycle through. Excludes
 * disabled controls and anything explicitly removed from the tab order
 * (`tabindex="-1"`). Kept here so the component and any test share one source.
 */
export const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Given the ordered focusable elements inside a modal, the currently-focused
 * element, and whether Shift is held, return the element focus should wrap to
 * — or `null` to let the browser move focus normally (mid-list Tab).
 *
 * - Forward Tab on the last element → first (wrap).
 * - Shift+Tab on the first element → last (wrap).
 * - Focus outside the trap (e.g. on the dialog container) → pulled to an edge.
 * - Anywhere in the middle → `null` (no interference).
 * - A single focusable already focused → `null`. The element must NOT move
 *   (returning it would re-`focus()` on every Tab, flashing the focus ring);
 *   the caller still `preventDefault()`s to keep focus from leaving the trap.
 */
export function getTrapWrapTarget(
  focusables: readonly HTMLElement[],
  active: Element | null,
  shiftKey: boolean,
): HTMLElement | null {
  if (focusables.length === 0) return null;

  const first = focusables[0];
  const last = focusables[focusables.length - 1];

  // Focus has left (or never entered) the known set — pull it back to an edge.
  if (active === null || !focusables.includes(active as HTMLElement)) {
    return shiftKey ? last : first;
  }

  // Edge wraps only when there is somewhere else to wrap to (first !== last).
  if (shiftKey && active === first && first !== last) return last;
  if (!shiftKey && active === last && first !== last) return first;

  return null;
}
